import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import ProtectedRoute from "./components/app/ProtectedRoute";
import AuthCallback from "./components/app/AuthCallback";
import CookieBanner from "./components/app/CookieBanner";
import CrispChat from "./components/app/CrispChat";
import { initAnalytics, analytics } from "./lib/analytics";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import QuizPractice from "./pages/QuizPractice";
import QuizRunner from "./pages/QuizRunner";
import MockExam from "./pages/MockExam";
import Results from "./pages/Results";
import Tasks from "./pages/Tasks";
import StudyPlanPage from "./pages/StudyPlanPage";
import ProgressPage from "./pages/ProgressPage";
import Profile from "./pages/Profile";
import Review from "./pages/Review";
import AdminPanel from "./pages/AdminPanel";
import Affiliate from "./pages/Affiliate";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FeedbackWidget from "./components/app/FeedbackWidget";
import AnalyticsPixels from "./components/app/AnalyticsPixels";
import { FAQPage, ContactPage, ContentPage } from "./pages/PublicPages";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

function AppRouter() {
  const location = useLocation();
  // Initialise analytics once on mount; track route changes.
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);
  // CRITICAL: Detect session_id during render to avoid race conditions with ProtectedRoute / AuthProvider
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/practice" element={<ProtectedRoute><QuizPractice /></ProtectedRoute>} />
      <Route path="/quiz/run" element={<ProtectedRoute><QuizRunner /></ProtectedRoute>} />
      <Route path="/mock" element={<ProtectedRoute><MockExam /></ProtectedRoute>} />
      <Route path="/results/:attemptId" element={<ProtectedRoute><Results /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
      <Route path="/study-plan" element={<ProtectedRoute><StudyPlanPage /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><Review /></ProtectedRoute>} />
      <Route path="/about" element={<ContentPage slug="about" />} />
      <Route path="/privacy" element={<ContentPage slug="privacy" />} />
      <Route path="/kvkk" element={<ContentPage slug="kvkk" />} />
      <Route path="/terms" element={<ContentPage slug="terms" />} />
      <Route path="/distance-sales" element={<ContentPage slug="distance_sales" />} />
      <Route path="/refund" element={<ContentPage slug="refund" />} />
      <Route path="/cookies" element={<ContentPage slug="cookies" />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <AppRouter />
              <AnalyticsPixels />
              <CookieBanner />
              <CrispChat />
              <FeedbackWidget />
              <Toaster position="top-right" richColors closeButton />
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
